import { action } from '@ember/object';
import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import {
  task,
  type TaskForAsyncTaskFunction,
  type TaskInstance,
} from 'ember-concurrency';
import type {
  ConditionalStrategy,
  PrimaWarningValue,
  RecommendationStrategy,
} from 'lumen/models';
import type AccessService from 'lumen/services/access';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { Transition } from 'lumen/services/transition';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import getAPIErrorMessage from 'lumen/utils/get-api-error-message';

type TransitionWithIsAborted = Transition & { isAborted: boolean };

export interface RecommendationsModel {
  pricingAssistantRecommendationsStrategiesTask: TaskInstance<
    RecommendationStrategy[] | null
  >;
  primaWarningValuesTask: TaskInstance<PrimaWarningValue[] | null>;
  roomOffsetHierarchy: string[];
  saveStrategyTask: TaskForAsyncTaskFunction<
    { drop: boolean },
    (
      strategyRecord: RecommendationStrategy,
    ) => Promise<{ isSuccess: boolean } | null>
  >;
}

export default class HotelSettingsRecommendationsRoute extends Route {
  @service declare access: AccessService;
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare navigation: Services['navigation'];
  @service
  declare pricingAssistantDataRequestService: Services['pricing-assistant-data-request-service'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];

  confirmLeaveTask = task(
    { drop: true },
    async (transition: TransitionWithIsAborted) => {
      const shouldLeave = await this.alerting.showWarningAlert({
        title: this.intl.t('pricing_assistant.warning_banner.header'),
        dangerMode: true,
        buttons: {
          cancel: {
            text: this.intl.t(
              'pricing_assistant.warning_banner.secondary_button.label',
            ),
          },
          confirm: {
            text: this.intl.t(
              'pricing_assistant.warning_banner.primary_button.label',
            ),
          },
        },
      });

      if (shouldLeave) {
        // The target route may redirect, which re-enters willTransition; records must be clean by then.
        this.discardUnsavedChanges();
        transition.retry();
      }
    },
  );
  primaWarningValuesTask = task({ restartable: true }, async () => {
    let apiResponse = null;

    try {
      apiResponse = await this.store.query<PrimaWarningValue>(
        'prima-warning-value',
        {
          namespace: APIGATEWAY,
          typeKey: 'pricing-assistant/rates/warnings',
          subscription_id: this.hotelAndMarketService.hotel.id,
        },
      );

      return [...apiResponse];
    } catch (error) {
      this.basicErrorHandling(error);
    }

    return null; // in this case something went wrong with the request
  });

  saveStrategyTask = task({ drop: true }, async (strategyRecord) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      await strategyRecord.save();

      return {
        isSuccess: true,
      };
    } catch (error) {
      this.basicErrorHandling(error);
      return {
        isSuccess: false,
      };
    }
  });

  private get hasDirtyState(): boolean {
    const hasDirtyRecommendations = [
      ...this.store.peekAll<RecommendationStrategy>('recommendation-strategy'),
    ].some((r) => r.hasDirtyAttributes);

    const hasDirtyConditionals = [
      ...this.store.peekAll<ConditionalStrategy>('conditional-strategy'),
    ].some((r) => r.hasDirtyAttributes || r.isNew);

    return hasDirtyRecommendations || hasDirtyConditionals;
  }

  basicErrorHandling(error: unknown): void {
    if (this.access.isStaffUser && console) {
      console.error(error);
    }

    if (error) {
      this.errorTracking.captureException(error);
    }

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.alerting.showErrorAlert({
      text: getAPIErrorMessage(error, this.intl),
    });
  }

  beforeModel(transition: Transition): void {
    if (this.navigation.redirectToAccountSelection(transition)) {
      return;
    }

    if (!this.access.hasPricingAssistantTab) {
      this.router.transitionTo('hotel.settings.index');
    }
  }

  private discardUnsavedChanges(): void {
    const records = [
      ...this.store.peekAll<ConditionalStrategy>('conditional-strategy'),
      ...this.store.peekAll<RecommendationStrategy>('recommendation-strategy'),
    ];

    for (const record of records) {
      if (record.hasDirtyAttributes) {
        record.rollbackAttributes();
      }
    }
  }

  async model(): Promise<RecommendationsModel> {
    /**
     * This one gets awaited explicitly because it uses the same task as recommendationsStrategiesTask under the hood.
     * That task is set as restartable, so if both would be called simultaneously, one would cancel the other.
     */
    const roomOffsetHierarchy =
      await this.pricingAssistantDataRequestService.roomOffsetHierarchyTask.perform();

    const model: RecommendationsModel = {
      roomOffsetHierarchy,
      pricingAssistantRecommendationsStrategiesTask:
        this.pricingAssistantDataRequestService.recommendationsStrategiesTask.perform(),
      saveStrategyTask: this.saveStrategyTask,
      primaWarningValuesTask: this.primaWarningValuesTask.perform(),
    };

    return model;
  }

  resetController(controller: unknown, isExiting: boolean): void {
    if (isExiting) {
      this.store.unloadAll('recommendation-strategy');
      this.store.unloadAll('conditional-strategy');
      this.store.unloadAll('prima-warning-value');
    }
  }

  @action
  willTransition(transition: TransitionWithIsAborted): void {
    if (transition.isAborted || !this.hasDirtyState) {
      return;
    }

    // Abort immediately — required because the alert is async and the transition would otherwise
    // complete before the user has a chance to respond.
    transition.abort();
    void this.confirmLeaveTask.perform(transition);
  }
}
